import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { MoreHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import {
  useCustomerTeams,
  useInviteTeamMember,
  useRemoveTeamMember,
  useRevokeTeamInvitation,
  useResendTeamInvitation,
  useTeamInvitations,
  useTeamMemberships,
  useTeamMetrics,
  useUpdateTeamMemberRole,
} from '@/hooks/customer/useCustomerTeams';
import { CustomerTeamMember, CustomerTeamRole } from '@/types/customer-teams';
import { cn } from '@/lib/utils';
import { normalizeLanguageCode } from '@/lib/i18n/normalize-language';

const INVITABLE_ROLES = ['admin', 'member'] as const satisfies readonly CustomerTeamRole[];

const createInviteSchema = (t: TFunction) =>
  z.object({
    email: z.string().email({ message: t('customers.teams.validation.invalidEmail') }),
    role: z.enum(INVITABLE_ROLES),
    message: z
      .string()
      .max(280, t('customers.teams.validation.messageTooLong'))
      .optional()
      .or(z.literal('')),
  });

type InviteFormValues = z.infer<ReturnType<typeof createInviteSchema>>;

const createLimitUsageSchema = (t: TFunction) =>
  z
    .object({
      enabled: z.boolean(),
      creditLimit: z.string().optional(),
      emailAlerts: z.boolean(),
    })
    .superRefine((data, ctx) => {
      if (!data.enabled) {
        return;
      }

      if (!data.creditLimit || !data.creditLimit.trim().length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['creditLimit'],
          message: t('customers.teams.validation.creditRequired'),
        });
        return;
      }

      const parsed = Number(data.creditLimit);
      if (Number.isNaN(parsed) || parsed < 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['creditLimit'],
          message: t('customers.teams.validation.creditNonNegative'),
        });
      }
    });

type LimitUsageFormValues = z.infer<ReturnType<typeof createLimitUsageSchema>>;

const DEFAULT_USAGE_LIMIT_VALUES: LimitUsageFormValues = {
  enabled: true,
  creditLimit: '5000',
  emailAlerts: true,
};

export default function CustomerTeamsPage() {
  const { t, i18n } = useTranslation();
  const { user, provider } = useCustomerAuth();
  const providerName = provider?.name;
  const { data: teams, isLoading, isError } = useCustomerTeams();
  const [selectedTeamId, setSelectedTeamId] = useState<string | undefined>();

  useEffect(() => {
    if (!teams || teams.length === 0) {
      setSelectedTeamId(undefined);
      return;
    }
    if (!selectedTeamId || !teams.some((team) => team.id === selectedTeamId)) {
      setSelectedTeamId(teams[0]?.id);
    }
  }, [teams, selectedTeamId]);

  const selectedTeam = useMemo(() => teams?.find((team) => team.id === selectedTeamId) ?? teams?.[0], [teams, selectedTeamId]);

  const { data: members, isLoading: membersLoading } = useTeamMemberships(selectedTeam?.id);
  const { data: invitations, isLoading: invitationsLoading } = useTeamInvitations(selectedTeam?.id);
  const { data: metrics } = useTeamMetrics();

  const inviteSchema = useMemo(() => createInviteSchema(t), [t]);
  const inviteForm = useForm<InviteFormValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: '', role: 'member', message: '' },
  });

  const roleLabels = useMemo(
    () => ({
      owner: t('customers.teams.roles.owner'),
      admin: t('customers.teams.roles.admin'),
      member: t('customers.teams.roles.member'),
    }),
    [t]
  );

  const language = i18n.language ?? i18n.resolvedLanguage ?? 'en';
  const inviteLanguage = normalizeLanguageCode(language);
  const numberFormatter = useMemo(() => new Intl.NumberFormat(language), [language]);
  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(language, { dateStyle: 'medium' }),
    [language]
  );
  const dateTimeFormatter = useMemo(
    () => new Intl.DateTimeFormat(language, { dateStyle: 'short', timeStyle: 'short' }),
    [language]
  );

  const [limitUsageMember, setLimitUsageMember] = useState<CustomerTeamMember | null>(null);

  const inviteMember = useInviteTeamMember(selectedTeam?.id, {
    onSuccess: () => {
      inviteForm.reset({ email: '', role: 'member', message: '' });
    },
  });
  const updateMemberRole = useUpdateTeamMemberRole(selectedTeam?.id);
  const removeMember = useRemoveTeamMember(selectedTeam?.id);
  const resendInvitation = useResendTeamInvitation(selectedTeam?.id);
  const revokeInvitation = useRevokeTeamInvitation(selectedTeam?.id);

  const totalSeats = selectedTeam?.totalSeats ?? 0;
  const usedSeats = members?.filter((member) => member.status === 'active').length ?? 0;
  const seatUsage = totalSeats === 0 ? 0 : Math.min(100, Math.round((usedSeats / totalSeats) * 100));
  const hasSeatCapacity = totalSeats === 0 || usedSeats < totalSeats;
  const canManageMembers = selectedTeam?.role === 'owner' || selectedTeam?.role === 'admin';

  if (isLoading) {
    return <TeamsPageSkeleton />;
  }

  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('customers.teams.fallback.loadErrorTitle')}</CardTitle>
          <CardDescription>{t('customers.teams.fallback.loadErrorDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {t('customers.teams.fallback.loadErrorHelp')}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!teams?.length || !selectedTeam) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('customers.teams.fallback.emptyTitle')}</CardTitle>
          <CardDescription>{t('customers.teams.fallback.emptyDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {t('customers.teams.fallback.emptyHelp')}
          </p>
        </CardContent>
      </Card>
    );
  }

  const pendingInvitations = (invitations ?? []).filter((invite) => invite.status === 'pending');

  const invitePending = inviteMember.isPending;

  const handleLimitUsageSubmit = async (member: CustomerTeamMember, values: LimitUsageFormValues) => {
    const creditLimit = values.enabled ? Number(values.creditLimit) : null;
    await updateMemberRole.mutateAsync({
      customerId: member.customerId,
      role: member.role,
      creditLimit,
    });
  };

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">{t('customers.teams.title')}</h1>
            <p className="text-sm text-muted-foreground">
              {providerName
                ? t('customers.teams.subtitleWithProvider', { providerName })
                : t('customers.teams.subtitleWithoutProvider')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="team-selector" className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('customers.teams.activeTeamLabel')}
            </Label>
            <Select value={selectedTeam.id} onValueChange={setSelectedTeamId}>
              <SelectTrigger id="team-selector" className="w-56">
                <SelectValue placeholder={t('customers.teams.selectPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {teams?.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Card>
          <CardContent className="grid gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('customers.teams.seatUsageLabel')}
              </Label>
              <div className="flex items-end justify-between">
                <p className="text-2xl font-semibold">{usedSeats}&nbsp;/&nbsp;{totalSeats || '∞'}</p>
                <Badge variant="secondary" className="capitalize">
                  {roleLabels[selectedTeam.role]}
                </Badge>
              </div>
              <Progress value={seatUsage} aria-label={t('customers.teams.seatUsageLabel')} />
              <p className="text-xs text-muted-foreground">
                {t('customers.teams.seatUsageHelper', { percentage: seatUsage })}
              </p>
            </div>

            <div className="lg:col-start-2">
              <StatTile
                label={t('customers.teams.stats.activeMembers.label')}
                primary={membersLoading ? '—' : numberFormatter.format(usedSeats)}
                helper={t('customers.teams.stats.activeMembers.helper')}
              />
            </div>
            <StatTile
              label={t('customers.teams.stats.pendingInvitations.label')}
              primary={invitationsLoading ? '—' : numberFormatter.format(pendingInvitations.length)}
              helper={
                pendingInvitations.length
                  ? t('customers.teams.stats.pendingInvitations.helperPending')
                  : t('customers.teams.stats.pendingInvitations.helperEmpty')
              }
            />
          </CardContent>
        </Card>
      </section>

      <div className="grid gap-6 lg:grid-cols-[7fr_5fr]">
        <Card className="border-primary/20">
          <CardHeader className="space-y-1">
            <CardTitle>{t('customers.teams.members.title')}</CardTitle>
            <CardDescription>{t('customers.teams.members.description')}</CardDescription>
          </CardHeader>
            <CardContent className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('customers.teams.members.tableHeaders.name')}</TableHead>
                  <TableHead>{t('customers.teams.members.tableHeaders.role')}</TableHead>
                  <TableHead>{t('customers.teams.members.tableHeaders.status')}</TableHead>
                  <TableHead>{t('customers.teams.members.tableHeaders.credits')}</TableHead>
                  <TableHead>{t('customers.teams.members.tableHeaders.lastActive')}</TableHead>
                  <TableHead className="text-right">{t('customers.teams.members.tableHeaders.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(members ?? []).map((member) => {
                  const isCurrentUser = member.email.toLowerCase() === (user?.email ?? '').toLowerCase();
                  const disableRoleChange = member.role === 'owner';
                  const disableRemoval = member.role === 'owner' || isCurrentUser;
                  const rawStatus = (member.status ?? '').toLowerCase();
                  const statusText = rawStatus === 'active' ? t('common.active') : t('common.inactive');
                  const initialsSource = member.name && member.name !== 'Unknown member' ? member.name : member.email;
                  const avatarFallback = (initialsSource || '??')
                    .split(/\s+/)
                    .filter(Boolean)
                    .map((part: string) => part[0] ?? '')
                    .join('')
                    .slice(0, 2)
                    .toUpperCase() || '??';
                  const creditLimit =
                    member.creditLimit === undefined ? null : member.creditLimit === null ? null : member.creditLimit;
                  const creditUsage = member.creditUsage ?? 0;
                  const creditRemaining =
                    creditLimit !== null && creditLimit !== undefined
                      ? member.creditRemaining ?? Math.max(creditLimit - creditUsage, 0)
                      : undefined;
                  const limitReached = creditLimit !== null && creditLimit !== undefined && (creditRemaining ?? 0) <= 0;
                  const creditUsageText =
                    creditLimit !== null && creditLimit !== undefined
                      ? t('customers.teams.members.credits.usage', {
                          used: numberFormatter.format(creditUsage),
                          limit: numberFormatter.format(creditLimit),
                        })
                      : t('customers.teams.members.credits.usageUnlimited', {
                          used: numberFormatter.format(creditUsage),
                        });
                  const creditRemainingText =
                    creditLimit !== null && creditLimit !== undefined
                      ? limitReached
                        ? t('customers.teams.members.credits.limitReached')
                        : t('customers.teams.members.credits.remaining', {
                            remaining: numberFormatter.format(creditRemaining ?? 0),
                          })
                      : null;

                  return (
                    <TableRow key={member.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            {member.avatarUrl ? <AvatarImage src={member.avatarUrl} alt={member.name} /> : null}
                            <AvatarFallback>
                              {avatarFallback}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium leading-tight">{member.name}</p>
                            <p className="text-xs text-muted-foreground">{member.email || '—'}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={member.role}
                          disabled={disableRoleChange || updateMemberRole.isPending}
                          onValueChange={(role) =>
                            updateMemberRole.mutate({ customerId: member.customerId, role: role as CustomerTeamRole })
                          }
                        >
                          <SelectTrigger className="w-32 text-sm capitalize">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="owner">{roleLabels.owner}</SelectItem>
                            <SelectItem value="admin">{roleLabels.admin}</SelectItem>
                            <SelectItem value="member">{roleLabels.member}</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Badge variant={member.status === 'active' ? 'default' : 'outline'} className="capitalize">
                          {statusText}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 text-sm">
                          <span className="font-medium">{creditUsageText}</span>
                          {creditRemainingText ? (
                            <span
                              className={cn(
                                'block text-xs',
                                limitReached ? 'text-destructive' : 'text-muted-foreground'
                              )}
                            >
                              {creditRemainingText}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-muted-foreground">
                          {member.lastActive ? dateTimeFormatter.format(new Date(member.lastActive)) : '—'}
                        </p>
                      </TableCell>
                      <TableCell className="text-right">
                        <AlertDialog>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 rounded-full"
                                aria-label={t('customers.teams.members.actionsMenuAria')}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuLabel>{t('customers.teams.members.actionsLabel')}</DropdownMenuLabel>
                              <DropdownMenuItem onSelect={() => setLimitUsageMember(member)}>
                                {t('customers.teams.members.limitUsage')}
                              </DropdownMenuItem>
                              {canManageMembers ? (
                                <AlertDialogTrigger asChild>
                                  <DropdownMenuItem
                                    disabled={disableRemoval || removeMember.isPending}
                                    onSelect={(event) => {
                                      if (disableRemoval || removeMember.isPending) {
                                        event.preventDefault();
                                      }
                                    }}
                                    className={cn(disableRemoval && 'text-muted-foreground')}
                                  >
                                    {t('customers.teams.members.remove')}
                                  </DropdownMenuItem>
                                </AlertDialogTrigger>
                              ) : null}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          {canManageMembers ? (
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  {t('customers.teams.members.removeTitle', {
                                    memberName: member.name,
                                    teamName: selectedTeam.name,
                                  })}
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  {t('customers.teams.members.removeDescription')}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => removeMember.mutate(member.customerId)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  {t('customers.teams.members.removeConfirm')}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          ) : null}
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('customers.teams.invite.title')}</CardTitle>
              <CardDescription>{t('customers.teams.invite.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...inviteForm}>
                <form
                  className="space-y-4"
                  onSubmit={inviteForm.handleSubmit((values) =>
                    inviteMember.mutate({ ...values, language: inviteLanguage })
                  )}
                >
                  <FormField
                    control={inviteForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('customers.teams.invite.fields.email')}</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            {...field}
                            placeholder={t('customers.teams.invite.placeholder.email')}
                            disabled={invitePending || !hasSeatCapacity}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={inviteForm.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('customers.teams.invite.fields.role')}</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value} disabled={invitePending}>
                          <FormControl>
                            <SelectTrigger className="capitalize">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="admin">{roleLabels.admin}</SelectItem>
                            <SelectItem value="member">{roleLabels.member}</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={inviteForm.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('customers.teams.invite.fields.message')}</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder={t('customers.teams.invite.placeholder.message')}
                            disabled={invitePending}
                            rows={3}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {!hasSeatCapacity && (
                    <p className="text-sm text-destructive">{t('customers.teams.invite.seatLimitReached')}</p>
                  )}

                  <Button type="submit" className="w-full sm:w-auto" disabled={!hasSeatCapacity || invitePending}>
                    {invitePending ? t('customers.teams.invite.submitting') : t('customers.teams.invite.submit')}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="space-y-1">
              <CardTitle>{t('customers.teams.pending.title')}</CardTitle>
              <CardDescription>{t('customers.teams.pending.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {pendingInvitations.length === 0 && (
                <p className="text-sm text-muted-foreground">{t('customers.teams.pending.empty')}</p>
              )}

              {pendingInvitations.map((invitation) => {
                const normalizedStatus = invitation.status.toLowerCase();
                const statusText =
                  t(`customers.teams.pending.status.${normalizedStatus}`, {
                    defaultValue:
                      invitation.statusLabel ??
                      (normalizedStatus === 'pending' ? t('common.pending') : invitation.status),
                  });
                const invitedAtLabel = dateFormatter.format(new Date(invitation.invitedAt));
                const expiresLabel = invitation.expiresAt
                  ? t('customers.teams.pending.expires', {
                      date: dateFormatter.format(new Date(invitation.expiresAt)),
                    })
                  : '';
                return (
                  <div
                    key={invitation.id}
                    className="flex flex-col gap-3 rounded-lg border border-dashed p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{invitation.email}</span>
                        <Badge variant="outline" className="capitalize">
                          {roleLabels[invitation.role]}
                        </Badge>
                        <Badge
                          variant={
                            invitation.status === 'pending'
                              ? 'default'
                              : invitation.status === 'revoked'
                                ? 'destructive'
                                : 'secondary'
                          }
                          className="capitalize"
                        >
                          {statusText}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {t('customers.teams.pending.invitedBy', {
                          invitedBy: invitation.invitedBy,
                          invitedAt: invitedAtLabel,
                          expires: expiresLabel,
                        })}
                      </p>
                      {invitation.message ? (
                        <p className="text-xs text-muted-foreground">“{invitation.message}”</p>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={resendInvitation.isPending || revokeInvitation.isPending}
                        onClick={() => resendInvitation.mutate({ invitationId: invitation.id })}
                      >
                        {resendInvitation.isPending ? t('common.loading') : t('customers.teams.pending.actions.resend')}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        disabled={revokeInvitation.isPending || resendInvitation.isPending}
                        onClick={() => revokeInvitation.mutate({ invitationId: invitation.id })}
                      >
                        {revokeInvitation.isPending ? t('common.loading') : t('customers.teams.pending.actions.revoke')}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      </div>

      {metrics ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('customers.teams.snapshot.title')}</CardTitle>
            <CardDescription>{t('customers.teams.snapshot.description')}</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <SnapshotTile
              label={t('customers.teams.snapshot.totalCustomers')}
              value={numberFormatter.format(metrics.totalCustomers)}
            />
            <SnapshotTile
              label={t('customers.teams.snapshot.teamsCreated')}
              value={numberFormatter.format(metrics.totalTeams)}
            />
            <SnapshotTile
              label={t('customers.teams.snapshot.teamOwners')}
              value={numberFormatter.format(metrics.teamOwners)}
            />
            <SnapshotTile
              label={t('customers.teams.snapshot.teamMembers')}
              value={numberFormatter.format(metrics.teamMembers)}
              helper={t('customers.teams.snapshot.individualsHelper', {
                count: metrics.individualOnly,
                formattedCount: numberFormatter.format(metrics.individualOnly),
              })}
            />
          </CardContent>
        </Card>
      ) : null}

      <LimitUsageDialog
        member={limitUsageMember}
        open={Boolean(limitUsageMember)}
        onOpenChange={(open) => {
          if (!open) {
            setLimitUsageMember(null);
          }
        }}
        onSubmit={handleLimitUsageSubmit}
      />
    </div>
  );
}

interface LimitUsageDialogProps {
  member: CustomerTeamMember | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (member: CustomerTeamMember, values: LimitUsageFormValues) => Promise<void> | void;
}

function LimitUsageDialog({ member, open, onOpenChange, onSubmit }: LimitUsageDialogProps) {
  const { t } = useTranslation();
  const limitUsageSchema = useMemo(() => createLimitUsageSchema(t), [t]);
  const roleLabels = useMemo(
    () => ({
      owner: t('customers.teams.roles.owner'),
      admin: t('customers.teams.roles.admin'),
      member: t('customers.teams.roles.member'),
    }),
    [t]
  );
  const form = useForm<LimitUsageFormValues>({
    resolver: zodResolver(limitUsageSchema),
    defaultValues: DEFAULT_USAGE_LIMIT_VALUES,
  });

  useEffect(() => {
    if (open && member) {
      form.reset({
        enabled: member.creditLimit !== null && member.creditLimit !== undefined,
        creditLimit:
          member.creditLimit !== null && member.creditLimit !== undefined
            ? String(member.creditLimit)
            : DEFAULT_USAGE_LIMIT_VALUES.creditLimit,
        emailAlerts: true,
      });
    }
  }, [member, open, form]);

  const isEnabled = form.watch('enabled');
  const { isSubmitting } = form.formState;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader className="space-y-2">
          <DialogTitle>
            {member
              ? t('customers.teams.dialog.titleWithMember', { name: member.name || member.email })
              : t('customers.teams.dialog.title')}
          </DialogTitle>
          <DialogDescription>{t('customers.teams.dialog.description')}</DialogDescription>
        </DialogHeader>

        {member ? (
          <div className="flex items-center gap-3 rounded-lg border border-border/40 bg-muted/30 p-3">
            <Avatar className="h-11 w-11">
              {member.avatarUrl ? <AvatarImage src={member.avatarUrl} alt={member.name} /> : null}
              <AvatarFallback>
                {(member.name || member.email || '??')
                  .split(/\s+/)
                  .filter(Boolean)
                  .map((part: string) => part[0])
                  .join('')
                  .slice(0, 2)
                  .toUpperCase() || '??'}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-1">
              <p className="text-sm font-medium leading-tight">{member.name}</p>
              <p className="text-xs text-muted-foreground">{member.email}</p>
              <Badge variant="outline" className="capitalize">
                {roleLabels[member.role]}
              </Badge>
            </div>
          </div>
        ) : null}

        <Form {...form}>
          <form
            className="space-y-6"
            onSubmit={form.handleSubmit(async (values) => {
              if (!member) {
                return;
              }
              try {
                await onSubmit(member, values);
                onOpenChange(false);
              } catch (error) {
                // Errors surface through toast notifications; keep the dialog open for fixes.
              }
            })}
          >
            <FormField
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border border-border/40 bg-muted/20 p-4">
                  <div className="space-y-1">
                    <FormLabel>{t('customers.teams.dialog.enforceLabel')}</FormLabel>
                    <FormDescription>{t('customers.teams.dialog.enforceDescription')}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            {isEnabled ? (
              <FormField
                control={form.control}
                name="creditLimit"
                render={({ field }) => (
                  <FormItem className="sm:max-w-xs">
                    <FormLabel>{t('customers.teams.dialog.creditLabel')}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="1"
                        min={0}
                        placeholder="5000"
                        disabled={!isEnabled}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>{t('customers.teams.dialog.creditDescription')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}

            <FormField
              control={form.control}
              name="emailAlerts"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border border-border/40 bg-muted/20 p-4">
                  <div className="space-y-1">
                    <FormLabel>{t('customers.teams.dialog.emailAlertsLabel')}</FormLabel>
                    <FormDescription>{t('customers.teams.dialog.emailAlertsDescription')}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} disabled={!isEnabled} />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  {t('common.cancel')}
                </Button>
              </DialogClose>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('customers.teams.dialog.saving') : t('customers.teams.dialog.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function StatTile({
  label,
  primary,
  helper,
}: {
  label: string;
  primary: string;
  helper: string;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      <p className="text-lg font-semibold">{primary}</p>
      <p className="text-xs text-muted-foreground">{helper}</p>
    </div>
  );
}

function SnapshotTile({
  label,
  value,
  helper,
}: {
  label: string;
  value: number | string;
  helper?: string;
}) {
  return (
    <div className="rounded-lg border p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
      {helper ? <p className="text-xs text-muted-foreground">{helper}</p> : null}
    </div>
  );
}

function TeamsPageSkeleton() {
  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-28 w-full" />
      </div>
      <div className="grid gap-6 lg:grid-cols-[7fr_5fr]">
        <Skeleton className="h-[420px] w-full" />
        <div className="space-y-6">
          <Skeleton className="h-[260px] w-full" />
          <Skeleton className="h-[260px] w-full" />
        </div>
      </div>
    </div>
  );
}
